import { Box } from "@mui/material"
import { FilterAltOutlined } from "@mui-symbols-material/w300"
import { useNavigate } from "@tanstack/react-router"
import type { ChangeEvent, FC } from "react"
import { useState } from "react"

import { Button } from "@/components/common/Button"
import SearchInput from "@/components/SearchInput/SearchInput"
import PriceChangeHistoryFilterChips from "@/screens/Pricing/components/filterChips/PriceChangeHistoryFilterChips"
import PurchasingCostHistoryFilterDrawer from "@/screens/Pricing/components/filters/PurchasingCostHistoryFilterDrawer"
import { PriceChangeHistoryTable } from "@/screens/Pricing/components/PriceChangeHistoryTable"
import { PURCHASING_COST_FILTER_KEYS } from "@/screens/Pricing/constants"
import { useRevertPriceChangeSubmission } from "@/screens/Pricing/hooks/api/useRevertPriceChangeSubmission"
import { usePriceChangeHistoryColumns } from "@/screens/Pricing/hooks/usePriceChangeHistoryColumns"
import { usePriceChangeHistoryList } from "@/screens/Pricing/hooks/usePriceChangeHistoryList"

const PurchaseCostHistoryContainer: FC = () => {
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false)

  const navigate = useNavigate()

  const navigateToReviewSubmission = async (submissionId: string) => {
    await navigate({
      to: `/app/pricing/${submissionId}/review`,
    })
  }

  const {
    priceChangeHistoryItems,
    isFetching,
    searchParams,
    handleSearchChange,
    handleSortingChange,
    handleFilterRemove,
    tableContainerRef,
    sorting,
    handleScroll,
  } = usePriceChangeHistoryList({
    route: "/app/pricing/_layout/history/purchase-cost",
    filterKeys: PURCHASING_COST_FILTER_KEYS,
  })

  const { revertPriceChangeSubmission } = useRevertPriceChangeSubmission()

  const handleRevertPriceChangeSubmission = async (submissionId?: string) => {
    if (!submissionId) {
      return
    }

    const revertedSubmissionId = await revertPriceChangeSubmission({ submissionId })

    if (revertedSubmissionId) {
      await navigateToReviewSubmission(revertedSubmissionId)
    }
  }

  const handleTableRowClick = async (submissionId: string) => {
    await navigateToReviewSubmission(submissionId)
  }

  const columns = usePriceChangeHistoryColumns({ isPurchasingCost: true, onRevert: handleRevertPriceChangeSubmission })

  return (
    <Box className='flex h-full flex-col gap-y-6'>
      {/* Search */}
      <Box className='flex items-center gap-x-4'>
        <SearchInput
          placeholder='Search by Change ID, or Description...'
          defaultValue={searchParams.query}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleSearchChange(e.target.value)}
          className='w-full max-w-[524px]'
          data-testid='purchase-cost-history.list-page.search-field'
        />
        <Button
          appearance='outlined'
          startIcon={<FilterAltOutlined />}
          onClick={() => setIsFilterDrawerOpen(true)}
          data-testid='purchase-cost-history.list-page.filter-button'
        >
          Filter
        </Button>
      </Box>

      <PriceChangeHistoryFilterChips searchParams={searchParams} onClearFilterChip={handleFilterRemove} />

      <Box className='flex min-h-0 flex-1 overflow-y-auto'>
        <PriceChangeHistoryTable
          data={priceChangeHistoryItems}
          loading={isFetching}
          containerRef={tableContainerRef}
          sorting={sorting}
          columns={columns}
          onScroll={handleScroll}
          onSortingChange={handleSortingChange}
          onRowClick={handleTableRowClick}
        />
      </Box>

      <PurchasingCostHistoryFilterDrawer showFilterDrawer={isFilterDrawerOpen} toggleDrawer={setIsFilterDrawerOpen} />
    </Box>
  )
}

export default PurchaseCostHistoryContainer
